import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { CloseIcon, CloudUploadIcon, InfoCircle } from '../../../../assets/icons/index';
import LoaderIcon from '../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { MAX_EVENT_UPLOAD_FILES } from '../event-form.validation';
import {
  ACCEPTED_UPLOAD_EXTENSIONS,
  EventDocumentsFieldPath,
  useEventDocumentsBucket,
  UseEventDocumentsBucketParams,
} from '../hooks/use-event-documents-bucket';
import {
  EventFileDuplicateFeedback,
  EventFileUploadRenameModal,
  EventFileUploadStatusIcon,
} from './event-file-upload-duplicate-ui';

export type EventDocumentsBucketProps = {
  errorMessage?: string;
} & UseEventDocumentsBucketParams;

/**
 * The contents of one documents bucket: the uploaded rows, then the dropzone. Rendered inside an
 * `EventDocumentsAccordion`, which supplies the surrounding chrome.
 */
export const EventDocumentsBucket: FC<EventDocumentsBucketProps> = ({
  errorMessage,
  ...bucketParams
}) => {
  const {
    files,
    getInputProps,
    getRootProps,
    handleDeleteFile,
    isFileNameDuplicate,
    isReachedUploadMaximum,
    renameModal,
    renameModalFolderNames,
    resolveDisplayName,
    uploadedCount,
  } = useEventDocumentsBucket(bucketParams);

  return (
    <div className="flex w-full flex-col gap-3">
      <EventFileUploadRenameModal
        currentName={renameModal.currentName}
        folderNames={renameModalFolderNames}
        isOpen={renameModal.isOpenRenameModal}
        onClose={renameModal.handleCloseRenameModal}
        onOpenChange={renameModal.renameModalToggler}
        onSuccess={renameModal.handleSuccessRenameModal}
      />

      {files.length > 0 && (
        <div className="flex w-full flex-col gap-2">
          <span
            className={twMerge(
              'text-xs font-[450]',
              isReachedUploadMaximum ? 'text-sunbeam-500' : 'text-gray-500',
            )}
          >
            <AppFormattedMessage
              id="COUNT_OF_MAX_FILES_UPLOADED"
              values={{ count: uploadedCount, max: MAX_EVENT_UPLOAD_FILES }}
            />
          </span>
          {files.map(({ doc, docLink, id, loadProgress }) => {
            const isDuplicate = isFileNameDuplicate(doc, docLink);

            return (
              <div className="flex flex-col gap-2" key={id}>
                <div className="flex flex-col overflow-hidden rounded border border-brand-50 bg-brand-25">
                  <div className="flex items-center justify-between p-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <EventFileUploadStatusIcon
                        docLink={docLink}
                        isDuplicate={isDuplicate}
                        loadingIcon={<LoaderIcon className="size-4 animate-spin" />}
                      />
                      <span className="truncate text-xs font-[450] text-gray-700">
                        {resolveDisplayName(doc)}
                      </span>
                    </div>
                    <Button
                      className="w-fit p-1 hover:bg-gray-50"
                      onClick={() => handleDeleteFile(id || '')}
                      styleType="NONE"
                      type="button"
                    >
                      <CloseIcon className="size-2" />
                    </Button>
                  </div>
                  {/* Progress rides along the bottom edge of the row and disappears once the
                      upload resolves, so a settled row matches the design exactly. */}
                  <div
                    className={twMerge(
                      'h-[2px] shrink-0 bg-brand-700 transition-all duration-700',
                      docLink && 'bg-transparent',
                    )}
                    style={{ width: `${loadProgress || (docLink && 100) || 0}%` }}
                  />
                </div>
                <EventFileDuplicateFeedback
                  doc={doc}
                  docLink={docLink}
                  isDuplicate={isDuplicate}
                  onRename={renameModal.handleOpenRenameModal}
                />
              </div>
            );
          })}
        </div>
      )}

      <input
        {...getInputProps()}
        accept={ACCEPTED_UPLOAD_EXTENSIONS}
        className="hidden"
        multiple
        type="file"
      />
      <label
        {...getRootProps()}
        className={twMerge(
          'flex w-full items-center gap-6 rounded-lg border border-dashed border-gray-200 p-6',
          isReachedUploadMaximum ? 'cursor-auto' : 'cursor-pointer',
        )}
      >
        {errorMessage ? <InfoCircle className="size-14" thinIconVariant /> : <CloudUploadIcon />}
        <div className="flex flex-col gap-2">
          {errorMessage ? (
            <span className="text-sm font-[550] text-fireside-600">{errorMessage}</span>
          ) : isReachedUploadMaximum ? (
            // The dropzone goes inert at the limit; without this it just stopped responding.
            <p className="text-sm font-[450] text-gray-500">
              <AppFormattedMessage id="MAXIMUM_FILES" />
            </p>
          ) : (
            <p className="text-sm font-[450] text-gray-700">
              <span className="underline">
                <AppFormattedMessage id="CLICK_TO_UPLOAD" />
              </span>{' '}
              <AppFormattedMessage id="OR_DRAG_AND_DROP" />
            </p>
          )}
          <div className="flex flex-col gap-1 text-label-sm font-medium text-gray-500 opacity-70">
            <span>
              <AppFormattedMessage
                id="SUPPORTED_FORMATS"
                values={{ formats: 'doc/docx, xls/xlsx, csv, jpeg/jpg, png, pdf' }}
              />
            </span>
            <span>
              <AppFormattedMessage id="MAXIMUM_PER_FILE_SIZE" values={{ size: 10 }} />
            </span>
          </div>
        </div>
      </label>
    </div>
  );
};

export type { EventDocumentsFieldPath };
